import React from 'react';
import {
    Badge,
    Box,
    Container,
    Flex,
    Group,
    Loader,
    Paper,
    RingProgress,
    Stack,
    Text,
    ThemeIcon,
    Title,
} from '@mantine/core';
import { IconCalendarCheck, IconCalendarX } from '@tabler/icons-react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import { GET_DAILY_SALES_ACTIVITY } from '../../apollo/queries/dailySalesReview';
import { ProtectedRoute } from '../../components/auth';
import Navbar from '../../components/navigation/Navbar';
import ActivityGrid from './ActivityGrid';

type ActivityData = {
    userId: string;
    firstName: string;
    lastName: string;
    role?: string;
    storeNumber?: string;
    storeName?: string;
    divisionName?: string;
    activity: {
        date: string;
        reviewed: boolean;
        firstReviewedAt?: string | null;
        firstReviewedSource?:
            | 'DASHBOARD_DWELL'
            | 'EXPLICIT_CONFIRM'
            | 'EXPLICIT_CORRECTION'
            | null;
    }[];
    totalDays: number;
    reviewedDays: number;
    missedDays: number;
};

function StatCard({
    icon,
    iconColor,
    iconBg,
    label,
    value,
    valueColor,
}: {
    icon: React.ReactNode;
    iconColor: string;
    iconBg: string;
    label: string;
    value: string | number;
    valueColor?: string;
}) {
    return (
        <Paper shadow="sm" radius="lg" p="lg" withBorder>
            <Group gap="md">
                <ThemeIcon
                    size={48}
                    radius="xl"
                    variant="light"
                    color={iconColor}
                    style={{ backgroundColor: iconBg }}
                >
                    {icon}
                </ThemeIcon>
                <Box>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600} lh={1}>
                        {label}
                    </Text>
                    <Text
                        fw={700}
                        fz={28}
                        lh={1.2}
                        mt={4}
                        c={valueColor || '#002055'}
                    >
                        {value}
                    </Text>
                </Box>
            </Group>
        </Paper>
    );
}

function SalesReviewActivityPage() {
    const { userId } = useParams<{ userId: string }>();

    const { data, loading } = useQuery(GET_DAILY_SALES_ACTIVITY, {
        variables: { userId, days: 30 },
        skip: !userId,
    });

    const activityData: ActivityData | null =
        data?.getDailySalesActivity || null;

    const missedDays = activityData?.missedDays ?? 0;

    const percentage =
        activityData && activityData.totalDays > 0
            ? Math.round(
                  (activityData.reviewedDays / activityData.totalDays) * 100,
              )
            : 0;

    const getPercentageColor = () => {
        if (percentage >= 80) {
            return '#22C55E';
        }
        if (percentage >= 50) {
            return '#F59E0B';
        }
        return '#EF4444';
    };

    const locationInfo =
        activityData?.storeName || activityData?.divisionName || null;

    return (
        <ProtectedRoute>
            <div className="min-h-screen bg-[#f8f9fa]">
                <Navbar />
                <Container size="md" pt={80} pb={40}>
                    {loading && (
                        <Flex justify="center" align="center" h={400}>
                            <Loader size="lg" color="#004D94" />
                        </Flex>
                    )}

                    {!loading && !activityData && (
                        <Flex
                            justify="center"
                            align="center"
                            h={400}
                            direction="column"
                            gap="md"
                        >
                            <IconCalendarX
                                size={48}
                                color="var(--mantine-color-gray-5)"
                            />
                            <Text c="dimmed" size="lg">
                                No activity data found for this user.
                            </Text>
                        </Flex>
                    )}

                    {!loading && activityData && (
                        <Stack gap="xl">
                            <Paper
                                shadow="sm"
                                radius="lg"
                                p="xl"
                                withBorder
                                style={{
                                    borderLeft: '4px solid #004D94',
                                }}
                            >
                                <Group
                                    justify="space-between"
                                    align="flex-start"
                                >
                                    <Box>
                                        <Title order={2} c="#002055" lh={1.2}>
                                            {activityData.firstName}{' '}
                                            {activityData.lastName}
                                        </Title>
                                        {activityData.role && (
                                            <Text c="dimmed" size="md" mt={6}>
                                                {activityData.role}
                                            </Text>
                                        )}
                                        {locationInfo && (
                                            <Group gap="xs" mt={4}>
                                                {activityData.storeNumber && (
                                                    <Badge
                                                        variant="light"
                                                        color="#004D94"
                                                        size="sm"
                                                        radius="xl"
                                                    >
                                                        {
                                                            activityData.storeNumber
                                                        }
                                                    </Badge>
                                                )}
                                                <Text c="dimmed" size="sm">
                                                    {locationInfo}
                                                </Text>
                                            </Group>
                                        )}
                                    </Box>

                                    <RingProgress
                                        size={80}
                                        thickness={8}
                                        roundCaps
                                        sections={[
                                            {
                                                value: percentage,
                                                color: getPercentageColor(),
                                            },
                                        ]}
                                        label={
                                            <Text
                                                ta="center"
                                                fw={700}
                                                size="sm"
                                                c={getPercentageColor()}
                                            >
                                                {percentage}%
                                            </Text>
                                        }
                                    />
                                </Group>
                            </Paper>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <StatCard
                                    icon={
                                        <IconCalendarCheck
                                            size={24}
                                            stroke={1.5}
                                        />
                                    }
                                    iconColor="green"
                                    iconBg="rgba(34, 197, 94, 0.1)"
                                    label="Days Reviewed"
                                    value={activityData.reviewedDays}
                                />
                                <StatCard
                                    icon={
                                        <IconCalendarX size={24} stroke={1.5} />
                                    }
                                    iconColor="red"
                                    iconBg="rgba(239, 68, 68, 0.1)"
                                    label="Days Missed"
                                    value={missedDays}
                                />
                            </div>

                            <Paper shadow="sm" radius="lg" p="xl" withBorder>
                                <Title order={4} c="#002055" mb="lg">
                                    Last 30 Days
                                </Title>
                                <ActivityGrid
                                    activity={activityData.activity}
                                />
                            </Paper>
                        </Stack>
                    )}
                </Container>
            </div>
        </ProtectedRoute>
    );
}

export default SalesReviewActivityPage;
